import { format, parse } from 'date-fns';
import CheckInDateRangeFactory from '@/js/common/Domain/Factory/checkInDateRange/CheckInDateRange';
import CountryFactory from '@/js/common/Domain/Factory/Country';
import DepartureCityFactory from '@/js/common/Domain/Factory/DepartureCity';
import CountriesRepository from '@/js/common/Domain/Repository/Countries';
import DepartureCitiesRepository from '@/js/common/Domain/Repository/DepartureCities';
import MealsRepository from '@/js/common/Domain/Repository/Meals';
import OperatorsRepository from '@/js/common/Domain/Repository/Operators';
import errorHandlerService from '@/js/common/Domain/Service/errorHandler';

const CHECK_IN_DATE_RANGE_NOT_COMPUTED = Symbol('checkInDateRangeNotComputed');

const TOUR_DEFAULTS = {
    checkInDate: null,
    departureCity: null,
    country: null,
    discount: null,
    hotel: null,
    sortRate: null,
    meal: null,
    nights: null,
    nightsInHotel: null,
    operator: null,
    originalPrice: null,
    price: null,
    priceWithOilTax: null,
    resort: null,
    roomDescription: null,
    routes: null,
    oilTax: null,
    oilTaxIsAbsent: null,
    hotelAvailable: null,
    touristGroup: null,
    transfers: null,
    transfer: null,
    availableTransfers: null,
    lessPlaces: null,
    flightType: null,
    freeCancellation: false,
    freeReservationAvailable: false,
    roomGroups: [],
    block: false,
};

export default class TourFactoryOptimize {
    constructor(data, resorts) {
        Object.assign(this, TOUR_DEFAULTS);

        let isCheckInDateRangeEnabled = false;
        let element;
        let departureCity;
        let resort;
        let country;
        let meal;

        element = data.sortRate;
        if (element !== undefined) {
            this.sortRate = element;
        }

        element = data.nights;
        if (element !== undefined) {
            this.nights = element.tour;
            this.nightsInHotel = element.hotel;
        }

        element = data.checkInDate;
        if (element !== undefined && this.nights) {
            this.checkInDate = element;
            isCheckInDateRangeEnabled = true;
        }

        element = data.meal;
        if (element !== undefined) {
            meal = MealsRepository.repository.get(element);
            this.meal = meal || element;
        }

        element = data.departureCity;
        if (element !== undefined) {
            if (DepartureCitiesRepository.repository.length > 0) {
                departureCity = element.id ? DepartureCitiesRepository.repository.get(element.id) : null;
            }
            this.departureCity = departureCity || new DepartureCityFactory(element);
        }

        this.block = !!data.block;

        element = data.country;
        if (element !== undefined) {
            if (CountriesRepository.repository.length > 0) {
                country = element.id ? CountriesRepository.repository.get(element.id) : null;
            }
            this.country = country || new CountryFactory(element);
        }

        element = data.resort;
        if (element !== undefined) {
            resort = resorts?.get(element);

            if (resort === undefined && resorts?.length) {
                errorHandlerService(`No resort ${element.id}:${element.name} on hotel ${data.hotel.id}:${data.hotel.name} but found in tour. Resort: ${data.resort}`);
            }

            this.resort = resort || element;
        }

        element = data.hotel;
        if (element !== undefined) {
            this.hotel = element;

            element = data.hotelCategory?.name;
            if (element !== undefined) {
                this.hotel.hotelCategoryName = element;
            }
        }

        element = data.cashbackAvailable;
        if (element !== undefined) {
            this.cashbackAvailable = element;
        }

        element = data.operator;
        if (element !== undefined) {
            this.operator = OperatorsRepository.repository.add(element).toJSON() || null;
        }

        element = data.price;
        if (element !== undefined) {
            this.price = element;
        }

        element = data.oilTax;
        if (element !== undefined) {
            this.oilTax = element;
            this.oilTaxIsAbsent = element === null;
        }
        if (data.price) {
            this.priceWithOilTax = this.price + (this.oilTax || 0);
        }

        element = data.routes;
        if (element !== undefined) {
            this.routes = element;
        }

        element = data.room;
        if (element !== undefined) {
            this.room = element;
        }

        element = data.touristGroup;
        if (element !== undefined) {
            this.touristGroup = element;
        }

        element = data.transfers;
        if (element !== undefined) {
            this.transfers = element;
        }

        element = data.transfer;
        if (element !== undefined) {
            this.transfer = element;
        }

        element = data.hotelAvailable;
        if (element !== undefined) {
            this.hotelAvailable = element;
        }

        if ((data.hotelAvailable !== undefined && data.hotelAvailable === 2)
            || (data.isLessTicketsQty !== undefined && data.isLessTicketsQty)) {
            this.lessPlaces = true;
        }

        element = data.flightSpecificationPrice;
        if (element !== undefined) {
            this.flightSpecificationPrice = element;
        }

        element = data.freeCancellation;
        if (element) {
            this.freeCancellation = element;
        }

        element = data.freeReservationAvailable;
        if (typeof element === 'boolean') {
            this.freeReservationAvailable = element;
        }

        element = data.flightType;
        if (element) {
            this.flightType = element;
        }

        if (data.originalPrice && this.priceWithOilTax) {
            const { priceWithOilTax } = this;
            const { originalPrice } = data;
            this.originalPrice = originalPrice;
            this.discount = priceWithOilTax < originalPrice ? (1 - priceWithOilTax / originalPrice) : 0;
        }

        this.roomGroups = data?.roomGroups?.length
            ? data.roomGroups
            : [{ touristGroup: data.touristGroup, room: '', hash: '' }];

        Object.defineProperty(this, '_isCheckInDateRangeEnabled', {
            value: isCheckInDateRangeEnabled,
            enumerable: false,
        });
        Object.defineProperty(this, '_checkInDateRange', {
            value: CHECK_IN_DATE_RANGE_NOT_COMPUTED,
            writable: true,
            enumerable: false,
        });
    }

    get checkInDateRange() {
        if (!this._isCheckInDateRangeEnabled) {
            return;
        }
        if (this._checkInDateRange === CHECK_IN_DATE_RANGE_NOT_COMPUTED) {
            const dateParts = this.checkInDate.split('-');
            this._checkInDateRange = CheckInDateRangeFactory({
                from: this.checkInDate,
                to: new Date((new Date(dateParts[0], dateParts[1] - 1, dateParts[2])).getTime()
                    + this.nights * 3600 * 24 * 1000 + 3600 * 4 * 1000),
            });
        }
        return this._checkInDateRange;
    }

    get startDate() {
        const { routes, checkInDate } = this;
        const routeStartDate = routes && routes.length > 0 && routes[0].there?.legs?.[0].departureDate
            ? routes[0].there.legs[0].departureDate
            : null;

        if (routeStartDate) {
            return format(parse(routeStartDate, 'yyyy-MM-dd HH:mm:ss', new Date()), 'yyyy-MM-dd');
        }

        return checkInDate;
    }

    get paymentCashBackTour() {
        const { priceWithOilTax } = this;
        const maxCashBackMir = 20000;
        const paymentCashBackMirTour = Math.ceil(priceWithOilTax * 0.8);
        const cashBackMir = priceWithOilTax - paymentCashBackMirTour;
        return (cashBackMir > maxCashBackMir) ? priceWithOilTax - maxCashBackMir : paymentCashBackMirTour;
    }

    get(field) {
        return this[field];
    }

    set(field, value) {
        if (field !== null && typeof field === 'object') {
            Object.keys(field).forEach((key) => {
                this[key] = field[key];
            });
            return this;
        }
        this[field] = value;
        return this;
    }
    get attributes() {
        return this;
    }

    toJSON() {
        const result = {};
        Object.keys(this).forEach((key) => {
            result[key] = this[key];
        });
        return result;
    }
}
